import { decryptLocalData } from "@comps/commonFunctions";
import {
  getCompanies,
  getAllLocationsReq,
  getAllAreasReq,
  getAllBranches,
  getAllDepartments,
  getAllGrades,
  getAllDesignations,
  getAllJobTitlesReq,
  getEmployeesReq,
  getTimeSlotsReq,
  getRosterReq,
  getAttendanceConfigReq,
  getMachinesReq,
  getAdmsMachinesReq,
  getLeaveTypesPageReq,
  getLeaveQuotasReq,
  getLendCategoriesReq,
  getLendTypesReq,
  getRequestTypesReq,
  getRequestFlowReq,
  getPayrollsReq,
  getPayrollConfigReq,
} from "./requests";

export const SETUP_STATUS = {
  complete: "complete",
  required: "required",
  optional: "optional",
  skipped: "skipped",
  needsReview: "needs_review",
};

const pagePayload = { page: 1, filters: "" };

export const getOnboardingScopeKey = () => {
  const user = decryptLocalData("user") || {};
  const tenant = user.tenant || "tenant";
  const userId = user.id || user.user_id || user.employment_id || "user";
  const companyId = localStorage.getItem("company_id") || "no-company";
  return `klok_onboarding_draft_v2_${tenant}_${userId}_${companyId}`;
};

const safe = async (key, fn, fallback) => {
  try {
    const response = await fn();
    return { key, ok: true, data: response?.data ?? response };
  } catch (e) {
    return { key, ok: false, data: fallback };
  }
};

const arr = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const total = (value) => {
  if (typeof value?.total === "number") return value.total;
  if (typeof value?.meta?.pagination?.total === "number") return value.meta.pagination.total;
  if (typeof value?.pagination?.total === "number") return value.pagination.total;
  return arr(value).length;
};

const listData = (value) => {
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value)) return value;
  return [];
};

const countRosterShifts = (shifts) => {
  if (!shifts) return 0;
  if (Array.isArray(shifts)) return shifts.length;
  if (typeof shifts === "object") {
    return Object.values(shifts).reduce((sum, value) => {
      if (Array.isArray(value)) return sum + value.length;
      return sum + (value ? 1 : 0);
    }, 0);
  }
  return 0;
};

const hasConfig = (general) =>
  !!general && Object.values(general).some((value) => value !== null && value !== undefined && value !== "");

export const buildStepStatuses = (readiness, wizard = {}) => {
  const orgCounts = { ...(readiness.org.counts || {}), ...(wizard.org?.counts || {}) };
  const companyExists = readiness.company.exists || !!wizard.company?.id;
  const orgMissing = !companyExists || ["locations", "areas", "branches", "departments", "grades", "designations", "jobTitles"].some((key) => (orgCounts[key] || 0) < 1);
  const employeesMissing = readiness.employees.count < 1 && !wizard.employees?.imported && !wizard.employees?.existing;
  const optionalState = (key, complete) => {
    if (wizard.skipped?.[key]) return SETUP_STATUS.skipped;
    return complete ? SETUP_STATUS.complete : SETUP_STATUS.optional;
  };

  return {
    company: orgMissing ? SETUP_STATUS.required : SETUP_STATUS.complete,
    employees: employeesMissing ? SETUP_STATUS.required : SETUP_STATUS.complete,
    shifts: optionalState(
      "shifts",
      readiness.shifts.timeSlotCount > 0 || readiness.shifts.assignedCount > 0 || readiness.shifts.attendanceConfigured || wizard.shifts?.attendanceDone
    ),
    machines: optionalState("machines", readiness.machines.total > 0 || !!wizard.machines?.category),
    leave: optionalState("leave", readiness.leave.count > 0 || readiness.leave.quotaCount > 0 || (wizard.leave?.appliedCount || 0) > 0),
    loans: optionalState("loans", readiness.loans.categoryCount > 0 || readiness.loans.typeCount > 0),
    requestflow: optionalState("requestflow", readiness.requestflow.configuredCount > 0 || (wizard.requestflow?.configured || []).length > 0),
    payrollpolicies: optionalState("payrollpolicies", readiness.payroll.configured || readiness.payroll.payrollCount > 0),
    golive: orgMissing || employeesMissing ? SETUP_STATUS.needsReview : SETUP_STATUS.complete,
  };
};

export const getBlockingSetupItems = (readiness, wizard = {}) => {
  const blockers = [];
  const orgCounts = { ...(readiness.org.counts || {}), ...(wizard.org?.counts || {}) };
  const companyExists = readiness.company.exists || !!wizard.company?.id;
  const missingOrg = [
    !companyExists ? "Company" : null,
    (orgCounts.locations || 0) < 1 ? "Locations" : null,
    (orgCounts.areas || 0) < 1 ? "Areas" : null,
    (orgCounts.branches || 0) < 1 ? "Branches" : null,
    (orgCounts.departments || 0) < 1 ? "Departments" : null,
    (orgCounts.grades || 0) < 1 ? "Grades" : null,
    (orgCounts.designations || 0) < 1 ? "Designations" : null,
    (orgCounts.jobTitles || 0) < 1 ? "Job titles" : null,
  ].filter(Boolean);
  if (!companyExists) blockers.push("Create the company profile.");
  missingOrg.filter((label) => label !== "Company").forEach((label) => blockers.push(`Add at least one ${label.toLowerCase()}.`));
  if (readiness.employees.count < 1 && !wizard.employees?.imported && !wizard.employees?.existing) blockers.push("Add or import employees.");
  return blockers;
};

export const createEmptyReadiness = () => ({
  company: { exists: false, record: null },
  org: {
    counts: {
      locations: 0,
      areas: 0,
      branches: 0,
      departments: 0,
      grades: 0,
      designations: 0,
      jobTitles: 0,
    },
    missingRequired: ["Company", "Locations", "Areas", "Branches", "Departments", "Grades", "Designations", "Job titles"],
  },
  employees: { count: 0, rows: [], pagination: { total: 0, current_page: 1, per_page: 10 } },
  shifts: { timeSlotCount: 0, assignedCount: 0, attendanceConfigured: false },
  machines: { standardCount: 0, admsCount: 0, total: 0 },
  leave: { count: 0, quotaCount: 0, rows: [] },
  loans: { categoryCount: 0, typeCount: 0 },
  requestflow: { typeCount: 0, configuredCount: 0 },
  payroll: { configured: false, payrollCount: 0 },
  errors: {},
});

export const loadOnboardingReadiness = async (companyId) => {
  const responses = await Promise.all([
    safe("companies", () => getCompanies(""), {}),
    safe("locations", () => getAllLocationsReq(pagePayload), {}),
    safe("areas", () => getAllAreasReq(pagePayload), {}),
    safe("branches", () => getAllBranches(pagePayload), {}),
    safe("departments", () => getAllDepartments(pagePayload), {}),
    safe("grades", () => getAllGrades(pagePayload), {}),
    safe("designations", () => getAllDesignations(pagePayload), {}),
    safe("jobTitles", () => getAllJobTitlesReq(pagePayload), {}),
    safe("employees", () => getEmployeesReq({ page: 1, filters: "employee_status_|active", showActive: true }), {}),
    safe("timeSlots", () => getTimeSlotsReq(pagePayload), {}),
    safe("roster", () => getRosterReq(pagePayload), {}),
    safe("attendanceConfig", () => getAttendanceConfigReq(), {}),
    safe("machines", () => getMachinesReq(pagePayload), {}),
    safe("admsMachines", () => getAdmsMachinesReq(), []),
    safe("leaveTypes", () => getLeaveTypesPageReq(pagePayload), {}),
    safe("leaveQuotas", () => getLeaveQuotasReq(pagePayload), {}),
    safe("lendCategories", () => getLendCategoriesReq(pagePayload), {}),
    safe("lendTypes", () => getLendTypesReq(pagePayload), {}),
    safe("requestTypes", () => getRequestTypesReq(""), {}),
    safe("payrolls", () => getPayrollsReq(pagePayload), {}),
    safe("payrollConfig", () => getPayrollConfigReq(), {}),
  ]);

  const byKey = responses.reduce((acc, item) => ({ ...acc, [item.key]: item }), {});
  const data = Object.fromEntries(Object.entries(byKey).map(([key, value]) => [key, value.data]));
  const errors = Object.fromEntries(Object.entries(byKey).filter(([, value]) => !value.ok).map(([key]) => [key, true]));

  const company = data.companies?.companies?.data?.[0] || null;
  if (company?.id) localStorage.setItem("company_id", company.id);

  const counts = {
    locations: total(data.locations?.locations),
    areas: total(data.areas?.areas),
    branches: total(data.branches?.branchs),
    departments: total(data.departments?.departments),
    grades: total(data.grades?.grades),
    designations: total(data.designations?.designations),
    jobTitles: total(data.jobTitles?.job_titles),
  };

  const missingRequired = [
    !company?.id ? "Company" : null,
    counts.locations < 1 ? "Locations" : null,
    counts.areas < 1 ? "Areas" : null,
    counts.branches < 1 ? "Branches" : null,
    counts.departments < 1 ? "Departments" : null,
    counts.grades < 1 ? "Grades" : null,
    counts.designations < 1 ? "Designations" : null,
    counts.jobTitles < 1 ? "Job titles" : null,
  ].filter(Boolean);

  const employeeRows = listData(data.employees?.employee_employments);
  const employeeCount = total(data.employees?.employee_employments);
  const employeePagination = data.employees?.employee_employments?.meta?.pagination
    || data.employees?.employee_employments?.pagination
    || { total: employeeCount, current_page: 1, per_page: employeeRows.length || 10 };
  const requestTypes = arr(data.requestTypes?.request_types);
  const requestFlowCompanyId = companyId || company?.id || localStorage.getItem("company_id");

  let configuredCount = 0;
  if (requestFlowCompanyId && requestTypes.length) {
    const flows = await Promise.all(
      requestTypes.map((type) =>
        safe(
          `flow-${type.id || type.value}`,
          () => getRequestFlowReq({
            request_flow: {
              company_id: requestFlowCompanyId,
              type_id: type.id || type.value,
              for_node_id: null,
              for_employee_id: "",
              for_employment_id: "",
            },
          }),
          {}
        )
      )
    );
    configuredCount = flows.filter((item) => arr(item.data?.flow).length > 0).length;
  }

  const standardMachineCount = total(data.machines?.machines);
  const admsMachineCount = arr(data.admsMachines?.machines || data.admsMachines).length;
  const leaveRows = listData(data.leaveTypes?.leave_types || data.leaveTypes);
  const leaveCount = total(data.leaveTypes?.leave_types || data.leaveTypes);
  const leaveQuotaCount = total(data.leaveQuotas?.quota_adjustments || data.leaveQuotas);

  return {
    company: { exists: !!company?.id, record: company },
    org: { counts, missingRequired },
    employees: { count: employeeCount, rows: employeeRows, pagination: employeePagination },
    shifts: {
      timeSlotCount: total(data.timeSlots?.time_slots || data.timeSlots?.data?.time_slots),
      assignedCount: countRosterShifts(data.roster?.shifts),
      attendanceConfigured: hasConfig(data.attendanceConfig?.general),
    },
    machines: {
      standardCount: standardMachineCount,
      admsCount: admsMachineCount,
      total: standardMachineCount + admsMachineCount,
    },
    leave: { count: leaveCount, quotaCount: leaveQuotaCount, rows: leaveRows.slice(0, 5) },
    loans: {
      categoryCount: total(data.lendCategories?.lend_categorys),
      typeCount: total(data.lendTypes?.lend_types),
    },
    requestflow: {
      typeCount: requestTypes.length,
      configuredCount,
    },
    payroll: {
      configured: hasConfig(data.payrollConfig?.general),
      payrollCount: total(data.payrolls?.payrolls),
    },
    errors,
  };
};
